// frontend/src/utils/helpers.ts

import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

export function formatDateTime(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export function formatCurrency(amount: number, currency: string = 'RUB'): string {
  if (!amount) return 'Не указана'
  
  const formatter = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
  
  return formatter.format(amount)
}

export function truncateText(text: string, maxLength: number = 100): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

export function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    review: 'bg-blue-100 text-blue-800',
    invited: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    offer: 'bg-purple-100 text-purple-800',
    hired: 'bg-emerald-100 text-emerald-800',
    draft: 'bg-gray-100 text-gray-800',
    approved: 'bg-green-100 text-green-800'
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'На рассмотрении',
    review: 'Просмотрено',
    invited: 'Приглашение',
    rejected: 'Отказ',
    offer: 'Оффер',
    hired: 'Принят',
    draft: 'Черновик',
    approved: 'Одобрено'
  }
  return labels[status] || status
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      func(...args)
    }
    
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)
  }
}

export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || ''
}

export function isImageFile(filename: string): boolean {
  const extensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']
  return extensions.includes(getFileExtension(filename))
}

export function getInitialsAvatar(name: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase() || '?'
  }
  return name.slice(0, 2).toUpperCase()
}

export function generateRandomColor(): string {
  const colors = [
    '#2563eb', '#7c3aed', '#dc2626', '#16a34a',
    '#ea580c', '#0891b2', '#db2777', '#4f46e5'
  ]
  return colors[Math.floor(Math.random() * colors.length)] ?? '#2563eb'
}
